
import { supabase } from "@/integrations/supabase/client";
import { BuyerProfile, SavedProperty, SavedSearch } from "@/types/buyer";
import { logger } from "@/utils/logger";

export const buyerService = {
  // Fetch buyer profile
  async fetchBuyerProfile(userId: string): Promise<BuyerProfile | null> {
    try {
      const { data, error } = await supabase
        .from("buyer_profiles")
        .select("id, user_id, full_name, company_name, phone_number, email, deals_annually, preferred_markets, purchasing_type, average_close_time, address, city, state, zip_code, subscription_status, trial_end_date, profile_image_url, email_notifications_enabled, notification_frequency, sms_opt_in, created_at, updated_at")
        .eq("user_id", userId)
        .single();

      if (error && error.code !== 'PGRST116') {
        logger.error("Error fetching buyer profile", { error: error.message });
        return null;
      }

      if (data) {
        // Ensure purchasing_type is always an array and set default values for new fields
        const rawData = data as Record<string, any>;
        return {
          id: rawData.id,
          user_id: rawData.user_id,
          full_name: rawData.full_name,
          company_name: rawData.company_name,
          phone_number: rawData.phone_number,
          email: rawData.email,
          deals_annually: rawData.deals_annually,
          preferred_markets: rawData.preferred_markets,
          purchasing_type: Array.isArray(rawData.purchasing_type) ? rawData.purchasing_type : [],
          average_close_time: rawData.average_close_time,
          address: rawData.address || null,
          city: rawData.city || null,
          state: rawData.state || null,
          zip_code: rawData.zip_code || null,
          subscription_status: rawData.subscription_status,
          trial_end_date: rawData.trial_end_date,
          profile_image_url: rawData.profile_image_url,
          email_notifications_enabled: rawData.email_notifications_enabled ?? true,
          notification_frequency: rawData.notification_frequency || 'instant',
          sms_opt_in: rawData.sms_opt_in ?? false,
          created_at: rawData.created_at,
          updated_at: rawData.updated_at,
        } as BuyerProfile;
      }

      return null;
    } catch (error) {
      logger.error("Error fetching buyer profile", { error });
      return null;
    }
  },

  // Fetch saved properties
  async fetchSavedProperties(buyerId: string): Promise<SavedProperty[]> {
    try {
      const { data, error } = await supabase
        .from("saved_properties")
        .select(`
          *,
          property:properties(*)
        `)
        .eq("buyer_id", buyerId);

      if (error) {
        logger.error("Error fetching saved properties", { error: error.message });
        return [];
      }

      return data || [];
    } catch (error) {
      logger.error("Error fetching saved properties", { error });
      return [];
    }
  },

  // Fetch saved searches
  async fetchSavedSearches(buyerId: string): Promise<SavedSearch[]> {
    try {
      const { data, error } = await supabase
        .from("saved_searches")
        .select("*")
        .eq("buyer_id", buyerId)
        .order("created_at", { ascending: false });

      if (error) {
        logger.error("Error fetching saved searches", { error: error.message });
        return [];
      }

      return data || [];
    } catch (error) {
      logger.error("Error fetching saved searches", { error });
      return [];
    }
  },

  // Save property
  async saveProperty(buyerId: string, propertyId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from("saved_properties")
        .insert({
          buyer_id: buyerId,
          property_id: propertyId
        });

      if (error) {
        if (error.code === '23505') { // Unique constraint violation
          return { success: false, error: "This property is already in your saved list." };
        }
        throw error;
      }

      return { success: true };
    } catch (error: any) {
      logger.error("Failed to save property", { error: error.message });
      return { success: false, error: error.message || "Failed to save property." };
    }
  },

  // Unsave property
  async unsaveProperty(buyerId: string, propertyId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from("saved_properties")
        .delete()
        .eq("buyer_id", buyerId)
        .eq("property_id", propertyId);

      if (error) throw error;

      return { success: true };
    } catch (error: any) {
      logger.error("Failed to remove property", { error: error.message });
      return { success: false, error: error.message || "Failed to remove property." };
    }
  },

  // Save search
  async saveSearch(buyerId: string, name: string, criteria: any): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from("saved_searches")
        .insert({
          buyer_id: buyerId,
          search_name: name,
          criteria
        });

      if (error) throw error;

      return { success: true };
    } catch (error: any) {
      logger.error("Failed to save search", { error: error.message });
      if (error.message.includes('Cannot save more than 8 searches')) {
        return { success: false, error: "You can only save up to 8 searches. Please delete an existing search first." };
      }
      return { success: false, error: error.message || "Failed to save search." };
    }
  },

  // Delete search
  async deleteSearch(searchId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from("saved_searches")
        .delete()
        .eq("id", searchId);

      if (error) throw error;

      return { success: true };
    } catch (error: any) {
      logger.error("Failed to delete search", { error: error.message });
      return { success: false, error: error.message || "Failed to delete search." };
    }
  },

  // Update buyer profile
  async updateBuyerProfile(buyerId: string, updates: Partial<BuyerProfile>): Promise<{ success: boolean; error?: string }> {
    try {
      logger.debug("Updating buyer profile");
      
      // Transform the updates to match the database schema
      const dbUpdates: any = { ...updates };
      
      // Ensure purchasing_type is stored as JSON array if it exists
      if (updates.purchasing_type) {
        dbUpdates.purchasing_type = updates.purchasing_type;
      }
      
      const { data, error } = await supabase
        .from("buyer_profiles")
        .update(dbUpdates)
        .eq("id", buyerId)
        .select()
        .single();

      if (error) {
        logger.error("Database update error", { error: error.message });
        throw error;
      }

      logger.debug("Profile updated successfully");
      return { success: true };
    } catch (error: any) {
      logger.error("Update buyer profile error", { error: error.message });
      return { success: false, error: error.message || "Failed to update profile." };
    }
  },

  // Follow seller
  async followSeller(buyerId: string, sellerId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from("seller_followers")
        .insert({
          buyer_id: buyerId,
          seller_id: sellerId
        });

      if (error) {
        if (error.code === '23505') { // Unique constraint violation
          return { success: false, error: "You are already following this seller." };
        }
        throw error;
      }

      return { success: true };
    } catch (error: any) {
      logger.error("Failed to follow seller", { error: error.message });
      return { success: false, error: error.message || "Failed to follow seller." };
    }
  },

  // Unfollow seller
  async unfollowSeller(buyerId: string, sellerId: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from("seller_followers")
        .delete()
        .eq("buyer_id", buyerId)
        .eq("seller_id", sellerId);

      if (error) throw error;

      return { success: true };
    } catch (error: any) {
      logger.error("Failed to unfollow seller", { error: error.message });
      return { success: false, error: error.message || "Failed to unfollow seller." };
    }
  },

  // Check if following seller
  async isFollowingSeller(buyerId: string, sellerId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase
        .from("seller_followers")
        .select("id")
        .eq("buyer_id", buyerId)
        .eq("seller_id", sellerId)
        .maybeSingle();

      if (error) {
        logger.error("Error checking follow status", { error: error.message });
        return false;
      }

      return !!data;
    } catch (error) {
      logger.error("Error checking follow status", { error });
      return false;
    }
  },

  // Get followed sellers
  async getFollowedSellers(buyerId: string): Promise<any[]> {
    try {
      // Step 1: Fetch follower records without joining profiles (RLS allows this)
      const { data: followers, error: followersError } = await supabase
        .from("seller_followers")
        .select("seller_id, followed_at")
        .eq("buyer_id", buyerId)
        .order("followed_at", { ascending: false });

      if (followersError) {
        logger.error("Error fetching followed sellers", { error: followersError.message });
        return [];
      }

      if (!followers || followers.length === 0) {
        return [];
      }

      // Step 2: Get seller info using RPC (SECURITY DEFINER bypasses RLS)
      const sellersWithInfo = await Promise.all(
        followers.map(async (follow) => {
          // Get seller profile via RPC
          const { data: sellerData, error: sellerError } = await (supabase as any)
            .rpc("get_public_seller_info", { seller_user_id: follow.seller_id });

          if (sellerError || !sellerData || sellerData.length === 0) {
            logger.warn("Could not fetch seller info", { 
              sellerId: follow.seller_id, 
              error: sellerError?.message 
            });
            return null;
          }

          const sellerInfo = sellerData[0];

          // Count active properties for seller
          const { count } = await supabase
            .from("properties")
            .select("*", { count: 'exact', head: true })
            .eq("seller_id", follow.seller_id)
            .eq("status", "active");

          return {
            id: sellerInfo.id,
            full_name: sellerInfo.full_name,
            business_name: sellerInfo.business_name,
            profile_image_url: sellerInfo.profile_image_url,
            active_listings: count || 0,
            followed_at: follow.followed_at
          };
        })
      );

      // Filter out any null results (sellers that couldn't be fetched)
      return sellersWithInfo.filter(Boolean);
    } catch (error) {
      logger.error("Error fetching followed sellers", { error });
      return [];
    }
  }
};
